import { FileText, FolderOpen, Layers, Users } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FirewallAddressObjects from "./FirewallAddressObjects";
import FirewallServiceObjects from "./FirewallServiceObjects";
import FirewallGroups from "./FirewallGroups";
import FirewallAddressGroups from "./FirewallAddressGroups";

// ── Tab Config ────────────────────────────────────────────────────────────────
const TABS = [
    { id: "address_objects", label: "Address Objects", icon: Layers, component: FirewallAddressObjects },
    { id: "service_objects", label: "Service Objects", icon: FileText, component: FirewallServiceObjects },
    { id: "group_objects", label: "Groups", icon: Users, component: FirewallGroups },
    { id: "address_groups", label: "Address Groups", icon: FolderOpen, component: FirewallAddressGroups },
] as const;

type TabId = typeof TABS[number]["id"];
const VALID_IDS = TABS.map(t => t.id) as string[];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function FirewallObjectsMain({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const rawTab = searchParams.get("objects_tab");
    const activeTab: TabId = (rawTab && VALID_IDS.includes(rawTab) ? rawTab : "address_objects") as TabId;

    const setActiveTab = (id: TabId) => {
        setSearchParams(prev => {
            prev.set("objects_tab", id);
            return prev;
        });
    };

    const ActiveComponent = TABS.find(t => t.id === activeTab)!.component;

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-gray-700 mb-4">
                {TABS.map(({ id, label, icon: Icon }) => {
                    const isActive = activeTab === id;
                    return (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`relative flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-all
                                ${isActive
                                    ? "text-purple-400"
                                    : "text-gray-200 hover:text-gray-300"
                                }`}
                        >
                            <Icon size={12} />
                            {label}
                            {isActive && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500 rounded-t-full" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            <div className="animate-fade-in">
                <ActiveComponent asset_id={asset_id} client_id={client_id} />
            </div>
        </div>
    );
}
